import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Clock, ExternalLink, Newspaper, RefreshCw, TrendingUp } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'forex',       label_ar: 'فوركس',        label_en: 'Forex',       hint_ar: 'EUR/USD · GBP · JPY',   hint_en: 'EUR/USD · GBP · JPY' },
  { key: 'crypto',      label_ar: 'كريبتو',       label_en: 'Crypto',      hint_ar: 'BTC · ETH · SOL',       hint_en: 'BTC · ETH · SOL' },
  { key: 'stocks',      label_ar: 'أسهم',         label_en: 'Stocks',      hint_ar: 'AAPL · TSLA · NVDA',    hint_en: 'AAPL · TSLA · NVDA' },
  { key: 'commodities', label_ar: 'سلع',          label_en: 'Commodities', hint_ar: 'الذهب · النفط · الفضة', hint_en: 'Gold · Oil · Silver' },
  { key: 'macro',       label_ar: 'اقتصاد كلّي',  label_en: 'Macro',       hint_ar: 'الفدرالي · التضخم',    hint_en: 'Fed · CPI · GDP' },
];

function timeAgo(iso, isRTL) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!then) return '';
  const sec = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return isRTL ? `قبل ${sec} ث` : `${sec}s ago`;
  const m = Math.floor(sec / 60);
  if (m < 60) return isRTL ? `قبل ${m} د` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return isRTL ? `قبل ${h} س` : `${h}h ago`;
  const d = Math.floor(h / 24);
  return isRTL ? `قبل ${d} ي` : `${d}d ago`;
}

const SkeletonCard = () => (
  <div className="varo-card overflow-hidden animate-pulse">
    <div className="aspect-[16/10] bg-flame-50" />
    <div className="p-5 space-y-3">
      <div className="h-3 w-24 rounded bg-flame-100" />
      <div className="h-4 w-5/6 rounded bg-slate-200" />
      <div className="h-4 w-4/6 rounded bg-slate-200" />
      <div className="h-3 w-3/6 rounded bg-slate-100" />
    </div>
  </div>
);

const NewsCard = ({ article, isRTL, idx }) => {
  const hasImage = article.image_url && article.image_url.startsWith('http') && !article.image_url.endsWith('.html');
  const fallbackInitial = (article.publisher_name || 'V').slice(0, 1).toUpperCase();
  return (
    <motion.a
      href={article.article_url || '#'}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`news-card-${idx}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="group varo-card varo-card-hover overflow-hidden flex flex-col bg-white"
    >
      {/* Cover */}
      <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-flame-50 via-cream-50 to-white">
        {hasImage ? (
          <img
            src={article.image_url}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-flame-400 to-flame-600 flex items-center justify-center text-white text-2xl font-black shadow-lg">
              {fallbackInitial}
            </div>
          </div>
        )}
        {/* Publisher chip */}
        {article.publisher_name && (
          <div className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur border border-flame-100 shadow-sm`}>
            <div className="w-1.5 h-1.5 rounded-full bg-flame-500 animate-pulse" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-ink-700 truncate max-w-[120px]">
              {article.publisher_name}
            </span>
          </div>
        )}
      </div>
      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-[15px] font-black tracking-tight text-ink-900 leading-snug mb-2 line-clamp-3 group-hover:text-flame-700 transition-colors">
          {article.title}
        </h3>
        {article.description && (
          <p className="text-[13px] text-ink-600 leading-relaxed line-clamp-2 mb-3">
            {article.description}
          </p>
        )}
        <div className="mt-auto flex items-center justify-between pt-3 border-t border-flame-100/60">
          <span className="inline-flex items-center gap-1.5 text-[11px] text-ink-500">
            <Clock className="w-3 h-3" />
            {timeAgo(article.published_utc, isRTL)}
          </span>
          <span className="inline-flex items-center gap-1 text-[12px] font-bold text-flame-600 group-hover:gap-2 transition-all">
            {isRTL ? 'اقرأ المزيد' : 'Read more'}
            <ExternalLink className="w-3 h-3" />
          </span>
        </div>
      </div>
    </motion.a>
  );
};

const LiveNewsTabs = () => {
  const { isRTL } = useLanguage();
  const [activeKey, setActiveKey] = useState('forex');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const activeCategory = useMemo(
    () => CATEGORIES.find((c) => c.key === activeKey) || CATEGORIES[0],
    [activeKey]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async (silent = false) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const lang = isRTL ? 'ar' : 'en';
        const r = await fetch(`${API_URL}/api/news/feed?category=${activeKey}&limit=8&lang=${lang}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        setItems(data.items || []);
      } catch (e) {
        if (cancelled) return;
        setError(e.message || 'Failed to load news');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };
    load();
    const id = setInterval(() => load(true), 120000); // 2-min refresh
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [activeKey, isRTL]);

  return (
    <section
      className="relative py-20 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #FAF7F2 0%, #FFFFFF 50%, #FFF3E8 100%)',
      }}
      data-testid="live-news-section"
    >
      {/* Decorative grain */}
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 20% 20%, #BC3F05 0%, transparent 50%), radial-gradient(circle at 80% 80%, #BC3F05 0%, transparent 50%)'
      }} />

      <div className="relative max-w-[1240px] mx-auto px-6">
        {/* Section header — properly RTL-aligned */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className={`max-w-2xl ${isRTL ? 'me-auto ms-0 text-start' : 'ms-0 me-auto text-start'}`}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-flame-50 border border-flame-200 mb-5`}>
            <div className="relative">
              <div className="w-2 h-2 rounded-full bg-flame-500 animate-pulse" />
              <div className="absolute inset-0 w-2 h-2 rounded-full bg-flame-500 animate-ping" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-flame-700">
              {isRTL ? 'مباشر' : 'Live'}
            </span>
          </div>
          <h2 className="varo-display varo-display-md text-ink-900">
            {isRTL ? <>الأسواق <span className="varo-text-flame">الآن.</span></> : <>The markets, <span className="varo-text-flame">right now.</span></>}
          </h2>
          <p className="mt-4 text-ink-600 text-[15px] leading-relaxed">
            {isRTL
              ? 'أخبار مالية مباشرة من Reuters و Bloomberg و GlobeNewswire وأكثر — مترجمة للعربية ومُفلترة حسب فئة الأصول التي تهمّك.'
              : 'Live financial news from Reuters, Bloomberg, GlobeNewswire and more — filtered by the asset class that matters to you.'}
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-10 mb-8 flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none"
          data-testid="news-tabs"
        >
          {CATEGORIES.map((c) => {
            const active = c.key === activeKey;
            return (
              <button
                key={c.key}
                onClick={() => setActiveKey(c.key)}
                data-testid={`news-tab-${c.key}`}
                className={`shrink-0 inline-flex items-center gap-2 px-5 h-11 rounded-full text-[13px] font-bold transition-all duration-300 ${
                  active
                    ? 'bg-flame-500 text-white shadow-lg shadow-flame-500/30 scale-[1.02]'
                    : 'bg-white text-ink-700 border border-flame-100 hover:border-flame-300 hover:text-flame-600'
                }`}
              >
                {active && <TrendingUp className="w-3.5 h-3.5" />}
                <span>{isRTL ? c.label_ar : c.label_en}</span>
                <span className={`text-[10px] font-medium opacity-70 hidden md:inline ${active ? 'text-white' : 'text-ink-500'}`}>
                  · {isRTL ? c.hint_ar : c.hint_en}
                </span>
              </button>
            );
          })}
          {refreshing && (
            <span className="ms-auto inline-flex items-center gap-1.5 text-[11px] text-flame-600 font-bold">
              <RefreshCw className="w-3 h-3 animate-spin" />
              {isRTL ? 'تحديث...' : 'Refreshing…'}
            </span>
          )}
        </motion.div>

        {/* Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeKey}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {loading && items.length === 0 ? (
              [...Array(8)].map((_, i) => <SkeletonCard key={i} />)
            ) : error ? (
              <div className="col-span-full text-center py-16 text-ink-500">
                <Newspaper className="w-10 h-10 mx-auto mb-3 text-flame-300" />
                <p className="text-sm font-bold">{isRTL ? 'تعذّر تحميل الأخبار حالياً' : 'Could not load news right now'}</p>
              </div>
            ) : items.length === 0 ? (
              <div className="col-span-full text-center py-16 text-ink-500">
                <Newspaper className="w-10 h-10 mx-auto mb-3 text-flame-300" />
                <p className="text-sm font-bold">{isRTL ? 'لا توجد أخبار حالياً' : 'No news yet'}</p>
              </div>
            ) : (
              items.map((a, i) => <NewsCard key={a.id || i} article={a} idx={i} isRTL={isRTL} />)
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default LiveNewsTabs;
